import { devLog } from '../utils/devLog';
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../services/firebaseAuth';
import { getUserData, createUserIfNotExists } from '../services/userService';
import { toast } from 'react-toastify';
import { registerPushToken, unregisterPushToken, bindForegroundToasts } from '../services/messaging';
import { startElectronNotificationListener, stopElectronNotificationListener } from '../services/electronNotificationListener';
import { initVoiceDevice, destroyVoiceDevice } from '../services/voice/voiceCallService';
import { authenticatedFetch } from '../api/apiClient';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';
import { claimsDisagreeWithProfile, resolveEffectiveRole } from '../utils/sessionRole';
import { authResultIsCurrent } from './authSession';
import { syncAskSbIdentity } from '../services/askSb/conversationMemory';

// Create auth context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userClaims, setUserClaims] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [agreementStatusReady, setAgreementStatusReady] = useState(false);
  const forcedRefreshByUidRef = useRef({});
  const initialAuthResolvedRef = useRef(false);
  const previousUidRef = useRef(null);
  const voiceInitUidRef = useRef(null);
  const authSessionRef = useRef({ generation: 0, uid: '' });

  useEffect(() => {
    let didResolveInitial = false;
    const fallbackTimer = setTimeout(() => {
      if (didResolveInitial) return;
      console.error('[AUTH] authReady fallback triggered (onIdTokenChanged did not resolve in time)');
      setAgreementStatus((prev) => prev || { complete: true });
      setAgreementStatusReady(true);
      setLoading(false);
      setAuthReady(true);
    }, 6000);

    let unsubscribe = null;
    try {
      unsubscribe = onIdTokenChanged(auth, async user => {
        const session = {
          generation: authSessionRef.current.generation + 1,
          uid: user?.uid || '',
        };
        authSessionRef.current = session;
        const stillCurrent = () => authResultIsCurrent({
          generation: session.generation,
          activeGeneration: authSessionRef.current.generation,
          uid: session.uid,
          activeUid: auth.currentUser?.uid || '',
        });
        const settle = async (promise) => {
          const result = await promise;
          if (!stillCurrent()) {
            const stale = new Error('stale-auth-session');
            stale.code = 'stale-auth-session';
            throw stale;
          }
          return result;
        };

        const isTokenRefresh = initialAuthResolvedRef.current && user && previousUidRef.current === user?.uid;
        setCurrentUser(user);
        previousUidRef.current = user?.uid || null;
        if (!isTokenRefresh) {
          setAuthReady(false);
          setLoading(true);
        }

        if (user) {
          try {
            // 1. Ensure user exists in Firestore
            await settle(createUserIfNotExists(user));

            // 2. Always force-refresh once per uid so a just-changed role is not stuck
            // on the previous tenant_admin/admin claims for this browser session.
            let idTokenResult = await settle(user.getIdTokenResult());
            if (!forcedRefreshByUidRef.current[user.uid]) {
              forcedRefreshByUidRef.current[user.uid] = true;
              await settle(user.getIdToken(true));
              idTokenResult = await settle(user.getIdTokenResult());
            }
            setUserClaims(idTokenResult.claims);

            // 3. Load the user profile. This must not be required to obtain the ID token.
            let firestoreUserData = null;
            try {
              firestoreUserData = await settle(getUserData(user.uid, user));
              setUserData(firestoreUserData);
            } catch (userDocErr) {
              if (userDocErr?.code === 'stale-auth-session') throw userDocErr;
              console.warn('[AUTH] User profile unavailable from API:', userDocErr?.message || userDocErr);
            }

            const claimsRole = idTokenResult.claims?.role;
            const mismatchKey = `${user.uid}:${claimsRole || ''}:${firestoreUserData?.role || ''}`;
            if (
              claimsDisagreeWithProfile(claimsRole, firestoreUserData)
              && forcedRefreshByUidRef.current[mismatchKey] !== true
            ) {
              forcedRefreshByUidRef.current[mismatchKey] = true;
              await settle(user.getIdToken(true));
              idTokenResult = await settle(user.getIdTokenResult());
              setUserClaims(idTokenResult.claims);
            }

            // 3b. Agreement completion status (backend is authoritative; do not skip this)
            try {
              const agrResp = await settle(authenticatedFetch(API_ROUTES.AGREEMENTS_ME_STATUS, { authUser: user }));
              const agrJson = await settle(agrResp.json());
              setAgreementStatus(agrJson?.data || { complete: true });
            } catch (agrErr) {
              if (agrErr?.code === 'stale-auth-session') throw agrErr;
              console.warn('[AUTH] Agreement status unavailable; failing open:', agrErr?.message || 'request failed');
              if (stillCurrent()) setAgreementStatus({ complete: true });
            }
            if (!stillCurrent()) {
              const stale = new Error('stale-auth-session');
              stale.code = 'stale-auth-session';
              throw stale;
            }
            setAgreementStatusReady(true);

            // 4. Compare the token role with the loaded profile role only.
            // A missing profile is a failed load, not a role change. Tenant
            // membership roles stay separate from this check.
            if (claimsDisagreeWithProfile(idTokenResult.claims?.role, firestoreUserData)) {
              console.warn(
                '[AUTH] Role mismatch between token claims and profile'
              );
            }

            // 5. Check for missing role
            const role = resolveEffectiveRole(idTokenResult.claims?.role, firestoreUserData?.role);
            if (!role) {
              console.warn('[AUTH] User has no role assigned');
              toast.warning('Your account has no role assigned. Some features may be restricted.');
            }

            // 6. Store credentials for offline flow cache (Electron only)
            try {
              if (window.electronAPI?.offlineFlow) {
                const token = await settle(user.getIdToken());
                const tenantId = idTokenResult.claims?.tenantId || firestoreUserData?.tenantId;
                
                if (tenantId) {
                  devLog('[AUTH] Storing credentials for offline flow cache');
                  await settle(window.electronAPI.offlineFlow.storeCredentials({
                    tenantId: tenantId,
                    userId: user.uid,
                    token: token,
                    firebaseToken: token
                  }));
                  
                  // Trigger immediate cache refresh
                  devLog('[AUTH] Refreshing offline flow cache');
                  const refreshResult = await settle(window.electronAPI.offlineFlow.refreshCache({
                    tenantId: tenantId,
                    userId: user.uid,
                    token: token,
                    firebaseToken: token
                  }));
                  
                  if (refreshResult.success) {
                    devLog('[AUTH] Offline flow cache refreshed successfully');
                  } else {
                    console.warn('[AUTH] Offline flow cache refresh failed');
                  }
                } else {
                  console.warn('[AUTH] No tenantId available for offline cache');
                }
              }
            } catch (cacheError) {
              if (cacheError?.code === 'stale-auth-session') throw cacheError;
              console.warn('[AUTH] Failed to update offline cache:', cacheError.message);
            }

            const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

            // 7. Mark auth as ready
            if (!stillCurrent()) return;
            didResolveInitial = true;
            initialAuthResolvedRef.current = true;
            clearTimeout(fallbackTimer);
            setLoading(false);
            setAuthReady(true);

            if (isElectron) {
              devLog('[AUTH] Electron detected, skipping FCM foreground binding and push token registration');
            } else {
              try { await settle(bindForegroundToasts()); } catch (e) {
                if (e?.code === 'stale-auth-session') throw e;
                console.error('[AUTH] bindForegroundToasts failed:', e?.message || 'request failed');
              }
              try {
                await settle(registerPushToken());
              } catch (e) {
                if (e?.code === 'stale-auth-session') throw e;
                console.error('[AUTH] registerPushToken failed:', e?.message || 'request failed');
              }
            }

            // Electron: start Firestore onSnapshot listener for real-time notifications
            try {
              const tid = idTokenResult.claims?.tenantId || firestoreUserData?.tenantId;
              if (tid && isElectron) {
                startElectronNotificationListener({ tenantId: tid, userId: user.uid });
              }
            } catch (e) { console.error('[AUTH] electronNotificationListener failed:', e?.message || 'request failed'); }

            // Twilio Voice SDK — init once per uid (not on every token refresh).
            if (stillCurrent() && voiceInitUidRef.current !== user.uid) {
              voiceInitUidRef.current = user.uid;
              try {
                await settle(initVoiceDevice(user.uid));
              } catch (voiceErr) {
                if (voiceErr?.code === 'stale-auth-session') throw voiceErr;
                console.warn('[AUTH] Voice device init failed:', voiceErr?.message || 'unknown error');
                voiceInitUidRef.current = null;
              }
            }
          } catch (error) {
            if (!stillCurrent() || error?.code === 'stale-auth-session') return;
            console.error('[AUTH] Error:', error.message);
            toast.error(`Authentication error: ${error.message}`);
            try {
              const agrResp = await authenticatedFetch(API_ROUTES.AGREEMENTS_ME_STATUS, { authUser: user });
              const agrJson = await agrResp.json();
              setAgreementStatus(agrJson?.data || { complete: true });
            } catch {
              setAgreementStatus({ complete: true });
            }
            setAgreementStatusReady(true);
            didResolveInitial = true;
            clearTimeout(fallbackTimer);
            setLoading(false);
            setAuthReady(true);
          }
        } else {
          // Reset state when user signs out
          setUserClaims(null);
          setUserData(null);
          setAgreementStatus(null);
          setAgreementStatusReady(true);
          voiceInitUidRef.current = null;
          didResolveInitial = true;
          clearTimeout(fallbackTimer);
          setLoading(false);
          setAuthReady(true);
          try { await unregisterPushToken(); } catch {}
          try { stopElectronNotificationListener(); } catch {}
          try { await destroyVoiceDevice(); } catch {}
        }
      });
    } catch (e) {
      console.error('[AUTH] Failed to subscribe to onIdTokenChanged:', e && e.message ? e.message : e);
      didResolveInitial = true;
      clearTimeout(fallbackTimer);
      setLoading(false);
      setAuthReady(true);
    }

    return () => unsubscribe();
  }, []);

  const refreshAgreementStatus = useCallback(async () => {
    const session = {
      generation: authSessionRef.current.generation,
      uid: auth.currentUser?.uid || '',
    };
    if (!session.uid) {
      setAgreementStatus(null);
      setAgreementStatusReady(true);
      return null;
    }
    try {
      const agrResp = await authenticatedFetch(API_ROUTES.AGREEMENTS_ME_STATUS, {
        authUser: auth.currentUser,
      });
      const agrJson = await agrResp.json();
      if (!authResultIsCurrent({
        generation: session.generation,
        activeGeneration: authSessionRef.current.generation,
        uid: session.uid,
        activeUid: auth.currentUser?.uid || '',
      })) return null;
      const next = agrJson?.data || { complete: true };
      setAgreementStatus(next);
      setAgreementStatusReady(true);
      return next;
    } catch (agrErr) {
      console.warn('[AUTH] refreshAgreementStatus failed open:', agrErr?.message || 'request failed');
      setAgreementStatus((prev) => prev || { complete: true });
      setAgreementStatusReady(true);
      return null;
    }
  }, []);

  const tenantId = userClaims?.tenantId || userData?.tenantId;

  useEffect(() => {
    import('../services/homeLauncher/launcherIconCache')
      .then(mod => {
        if (!currentUser?.uid) {
          mod.resetLauncherIconSession();
          return;
        }
        mod.syncLauncherIconContext(
          `${currentUser.uid}|${mod.launcherIconContextToken(tenantId)}`
        );
      })
      .catch(() => {});
  }, [currentUser, tenantId]);

  useEffect(() => {
    syncAskSbIdentity(currentUser?.uid || '', tenantId || '');
  }, [currentUser, tenantId]);

  const role = resolveEffectiveRole(userClaims?.role, userData?.role);
  const isAdmin = role === 'admin' || role === 'superadmin';

  const value = {
    currentUser,
    userClaims,
    userData,
    loading,
    authReady,
    isAdmin,
    tenantId,
    role,
    agreementStatus,
    agreementStatusReady,
    refreshAgreementStatus,
  };

  // Removed excessive auth logging

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
